/**
 * Create Admin User
 */
import { useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH } from "@jourfx/convex/lib/password";
import type { UserRole } from "@jourfx/types/users";
import { Button } from "@jourfx/ui";
import { useSession } from "../../lib/auth";

type AdminRole = Extract<UserRole, "administrator" | "super_admin">;
type OrganizationOption = { _id: string; name: string };

function getRoleHelp(
  t: (key: string) => string,
  role: AdminRole
): string {
  if (role === "super_admin") {
    return t("create.roleHelp.super_admin");
  }
  return t("create.roleHelp.administrator");
}

export function AdminCreate() {
  const { t } = useTranslation("admins");
  const navigate = useNavigate();
  const createAdmin = useMutation(api.admin.admins.create);
  const { isSuperAdmin } = useSession();
  const organizations = useQuery(
    api.admin.organizations.list,
    { status: "active", limit: 100 }
  ) as { organizations: OrganizationOption[] } | undefined;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("administrator");
  const [organizationId, setOrganizationId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t("create.passwordMismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await createAdmin({
        name,
        email,
        role,
        organizationId:
          role === "administrator" && isSuperAdmin
            ? (organizationId as any)
            : undefined,
        password,
      });
      navigate({ to: "/admins" });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("create.errorDefault"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admins" className="rounded-lg p-2 hover:bg-muted">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("create.title")}
          </h1>
          <p className="text-muted-foreground">{t("create.subtitle")}</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-xl rounded-lg border bg-card p-6"
      >
        <div className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-4 text-destructive">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-foreground"
            >
              {t("create.name")} {t("create.required")}
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("create.namePlaceholder")}
              required
              autoComplete="name"
              className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground"
            >
              {t("create.email")} {t("create.required")}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("create.emailPlaceholder")}
              required
              autoComplete="email"
              className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {isSuperAdmin ? (
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-foreground"
              >
                {t("create.role")} {t("create.required")}
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
                className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="administrator">
                  {t("create.roleOptions.administrator")}
                </option>
                <option value="super_admin">{t("create.roleOptions.super_admin")}</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {getRoleHelp(t, role)}
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-foreground">
                {t("create.role")}
              </label>
              <div className="mt-1 rounded-lg border border-input bg-muted px-3 py-2 text-sm text-foreground">
                {t("create.roleOptions.administrator")}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {getRoleHelp(t, "administrator")}
              </p>
            </div>
          )}

          {role === "administrator" && isSuperAdmin ? (
            <div>
              <label
                htmlFor="organizationId"
                className="block text-sm font-medium text-foreground"
              >
                {t("create.organization")} {t("create.required")}
              </label>
              <select
                id="organizationId"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                required
                className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">{t("create.organizationPlaceholder")}</option>
                {organizations?.organizations?.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-foreground"
            >
              {t("create.password")} {t("create.required")}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("create.passwordPlaceholder")}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("create.passwordHint", { min: MIN_PASSWORD_LENGTH })}
            </p>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-foreground"
            >
              {t("create.confirmPassword")} {t("create.required")}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t("create.confirmPasswordPlaceholder")}
              required
              minLength={MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Link to="/admins">
              <Button type="button" variant="outline">
                {t("create.cancel")}
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !name ||
                !email ||
                (role === "administrator" && isSuperAdmin && !organizationId) ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword
              }
            >
              {isSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("create.submit")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
